import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true';

function validatePassword(password, confirm) {
  const errors = [];
  if (password.length < 8) errors.push('At least 8 characters required.');
  if (!/[A-Z]/.test(password)) errors.push('Include at least one uppercase letter.');
  if (password !== confirm) errors.push('Passwords do not match.');
  return errors;
}

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const claimToken = searchParams.get('claim');

  async function handleSubmit(e) {
    e.preventDefault();
    setErrors([]);

    const validationErrors = validatePassword(password, confirm);
    if (!name.trim()) validationErrors.unshift('Name is required.');
    if (!email.trim()) validationErrors.unshift('Email is required.');

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    // Mock mode: navigate immediately
    if (MOCK_AUTH) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (!API_BASE_URL) {
      setErrors(['API not configured. Set VITE_API_BASE_URL or enable VITE_MOCK_AUTH=true.']);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), password, claimToken }),
        credentials: 'include',
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrors([data.error || 'Signup failed. Please try again.']);
        setLoading(false);
        return;
      }

      navigate('/dashboard', { replace: true });
    } catch {
      setErrors(['Network error. Check your connection and try again.']);
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: COLORS.black,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0A0A0A; }
      `}</style>

      <div style={{
        width: '100%',
        maxWidth: '440px',
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center' }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 900,
            letterSpacing: '0.15em',
            color: COLORS.white,
            margin: '0 0 0.75rem',
          }}>
            QRQY
          </h1>
          <h2 style={{
            fontSize: '1rem',
            fontWeight: 800,
            letterSpacing: '0.08em',
            color: COLORS.white,
            margin: '0 0 0.5rem',
          }}>
            CREATE YOUR ACCOUNT
          </h2>
          <p style={{
            fontSize: '0.8rem',
            color: COLORS.gray,
            lineHeight: 1.65,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}>
            Owning a QRQY starts here. Your account is how you control your piece.
          </p>
        </div>

        {/* Claim token notice */}
        {claimToken && (
          <div style={{
            padding: '1rem',
            border: `1px solid ${COLORS.gold}`,
            backgroundColor: 'rgba(201,168,76,0.06)',
          }}>
            <p style={{
              fontSize: '0.75rem',
              color: COLORS.gold,
              margin: 0,
              fontWeight: 600,
              letterSpacing: '0.05em',
            }}>
              A QRQY piece is waiting to be claimed. Complete your account to activate it.
            </p>
          </div>
        )}

        {/* Signup form */}
        <div style={{
          border: `1px solid rgba(255,255,255,0.1)`,
          padding: '2rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
        }}>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { label: 'YOUR NAME', value: name, setter: setName, type: 'text', placeholder: 'Full name', auto: 'name' },
              { label: 'EMAIL', value: email, setter: setEmail, type: 'email', placeholder: 'your@email.com', auto: 'email' },
              { label: 'PASSWORD', value: password, setter: setPassword, type: 'password', placeholder: '8+ characters, 1 uppercase', auto: 'new-password' },
              { label: 'CONFIRM PASSWORD', value: confirm, setter: setConfirm, type: 'password', placeholder: 'Repeat password', auto: 'new-password' },
            ].map(({ label, value, setter, type, placeholder, auto }) => (
              <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <label style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: COLORS.gray, fontWeight: 600 }}>
                  {label}
                </label>
                <input
                  type={type}
                  value={value}
                  onChange={(e) => setter(e.target.value)}
                  placeholder={placeholder}
                  autoComplete={auto}
                  style={{
                    padding: '0.85rem 1rem',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    border: `1px solid rgba(255,255,255,0.12)`,
                    color: COLORS.white,
                    fontSize: '0.88rem',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    outline: 'none',
                  }}
                />
              </div>
            ))}

            {errors.length > 0 && (
              <div style={{
                padding: '0.75rem',
                backgroundColor: 'rgba(224,92,92,0.08)',
                border: '1px solid rgba(224,92,92,0.2)',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.3rem',
              }}>
                {errors.map((err, i) => (
                  <p key={i} style={{ fontSize: '0.78rem', color: '#e05c5c', margin: 0 }}>
                    {err}
                  </p>
                ))}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.95rem',
                backgroundColor: COLORS.gold,
                color: COLORS.black,
                border: 'none',
                fontSize: '0.78rem',
                fontWeight: 800,
                letterSpacing: '0.2em',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                marginTop: '0.5rem',
              }}
            >
              {loading ? 'CREATING...' : 'CREATE ACCOUNT'}
            </button>
          </form>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: '0.78rem',
          color: COLORS.gray,
        }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: COLORS.white, textDecoration: 'underline', textUnderlineOffset: '3px' }}>
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
